import ko from 'knockout';
import moment from 'moment';

import localization from '@/global/localization';
import dateformat_to_moment from '@/util/dateformat_to_moment';

declare module 'moment' {
    interface Moment {
        dateonly: boolean;
        asLocaleDate(): string;
        asLocaleDateOnly(): string;
        asLocaleTimestamp(): string;
        asDate(): string;
        asDateOnly(): string;
        asTime(): string;
        asTimestamp(): string;
        asAge(): string;
    }
    function asLocaleDate(timestamp: TimestampLike): string;
    function asLocaleDateOnly(timestamp: TimestampLike): string;
    function asLocaleTimestamp(timestamp: TimestampLike): string;
    function fromDate(inp: MomentInput): Moment;
    function fromTime(inp: MomentInput): Moment;
    function fromDateOnly(inp: MomentInput): Moment;
}

moment.fn.toString = function () {
    const date = this.toDate();
    date.dateonly = this.dateonly;
    return date.toString();
};
moment.fn.toJSON = function () {
    return this.toString();
};

const dateformat = ko.pureComputed(() => {
    const loc = localization();
    return loc && dateformat_to_moment(loc.dateformat);
});

moment.fn.asLocaleDateOnly = function () {
    return this.format(dateformat());
};
moment.fn.asLocaleDate = function () {
    return this.format(`${dateformat()} HH:mm:ss`);
};
moment.fn.asLocaleTimestamp = function () {
    return this.format(`${dateformat()} HH:mm:ss.SSS`);
};

// These functions can be called with a timestamp-like object and will properly handle empty values,
// unlike `moment()` which defaults to the current time when the argument is empty.
moment.asLocaleDateOnly = function (timestamp: TimestampLike): string {
    const m = makeMoment(timestamp);
    return typeof m === 'string' ? m : m.asLocaleDateOnly();
};
moment.asLocaleDate = function (timestamp: TimestampLike): string {
    const m = makeMoment(timestamp);
    return typeof m === 'string' ? m : m.asLocaleDate();
};
moment.asLocaleTimestamp = function (timestamp: TimestampLike): string {
    const m = makeMoment(timestamp);
    return typeof m === 'string' ? m : m.asLocaleTimestamp();
};

moment.fn.asDateOnly = function () {
    return this.format('YYYY-MM-DD');
};
moment.fn.asTime = function () {
    return this.format('HH:mm:ss');
};
moment.fn.asDate = function () {
    return this.format('YYYY-MM-DD HH:mm:ss');
};
moment.fn.asTimestamp = function () {
    return this.format('YYYY-MM-DD HH:mm:ss.SSS');
};
moment.fn.asAge = function () {
    const display = (value: number, unit: string) => `${value} ${unit}` + (value === 1 ? '' : 's');
    const now = moment();
    return now.diff(this, 'y') >= 3
        ? display(now.diff(this, 'y'), 'year')
        : now.diff(this, 'M') >= 2
          ? display(now.diff(this, 'M'), 'month')
          : now.diff(this, 'w') >= 1
            ? display(now.diff(this, 'w'), 'week')
            : display(now.diff(this, 'd'), 'day');
};
moment.fromDateOnly = function (val) {
    return moment(val, 'YYYY-MM-DD');
};
moment.fromTime = function (val) {
    return moment(val, 'HH:mm:ss');
};
moment.fromDate = function (val) {
    return moment(val, 'YYYY-MM-DD HH:mm:ss');
};

Object.defineProperty(moment.fn, '_locale', {
    get: () => moment.localeData(),
    set: v => moment.locale(v._abbr),
});

/**
 * Parse the given argument into a valid moment object.
 * If parsing fails, a best-effort string representation of the argument is returned.
 * If the argument is empty, an empty string is returned.
 * @param timestamp - The argument to attempt to parse.
 */
function makeMoment(timestamp: TimestampLike): moment.Moment | string {
    if (!timestamp) {
        return '';
    }

    const m = moment(timestamp);

    // Valid moment - return it.
    if (m.isValid()) {
        return m;
    }
    // Invalid moment - try to return source string from timestamp parameter.
    const moment_src = (timestamp as { _i?: unknown })._i;
    timestamp = typeof moment_src === 'string' ? moment_src : timestamp;
    if (typeof timestamp === 'string') {
        return timestamp;
    }

    return 'Invalid Date'; // Should rarely happen.
}
type TimestampLike = string | moment.Moment | undefined;

export default moment;
